import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Loader2, Upload, File as FileIcon, Download, X, MessageSquare, Send, Trash2, StickyNote } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const docHref = (url) => (url?.startsWith('/api') ? `${BACKEND_URL}${url}` : `${API}${url}`);

const fmtDateTime = (s) => {
  try { return new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
};

const DocChips = ({ docs, onRemove, testidPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {docs.map((doc, i) => (
      <div key={i} className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1 text-xs" data-testid={`${testidPrefix}-${i}`}>
        <FileIcon className="h-3.5 w-3.5 text-amber-600" />
        <a href={docHref(doc.url)} target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline max-w-[160px] truncate flex items-center gap-1">
          {doc.filename}<Download className="h-3 w-3" />
        </a>
        {onRemove && (
          <button type="button" onClick={() => onRemove(i)} className="text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
    ))}
  </div>
);

const ServiceNotes = ({ serviceId }) => {
  const [notes, setNotes] = useState([]);
  const [canPost, setCanPost] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isManager, setIsManager] = useState(false);
  const [loading, setLoading] = useState(true);

  // New note state
  const [noteText, setNoteText] = useState('');
  const [noteDocs, setNoteDocs] = useState([]);
  const [uploadingNote, setUploadingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Per-note comment drafts: { [noteId]: { text, docs, uploading, saving } }
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/services/${serviceId}/notes`, { headers: getAuthHeaders() });
      setNotes(res.data?.notes || []);
      setCanPost(!!res.data?.can_post);
      setCurrentUserId(res.data?.current_user_id || null);
      setIsManager(!!res.data?.is_manager);
    } catch (err) {
      console.error('Notlar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (fileList, setUploading, appendFn) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('files', file);
        const res = await axios.post(`${API}/upload/offers`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
        const up = res.data?.files?.[0];
        if (up) appendFn({ filename: up.filename || file.name, url: up.url });
      }
      toast.success('Belge yüklendi');
    } catch { toast.error('Belge yüklenemedi'); }
    finally { setUploading(false); }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim() && noteDocs.length === 0) { toast.error('Not metni veya belge girin'); return; }
    setSavingNote(true);
    try {
      await axios.post(`${API}/services/${serviceId}/notes`, { text: noteText, documents: noteDocs }, { headers: getAuthHeaders() });
      setNoteText(''); setNoteDocs([]);
      toast.success('Not eklendi');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Not eklenemedi'); }
    finally { setSavingNote(false); }
  };

  const getDraft = (noteId) => drafts[noteId] || { text: '', docs: [], uploading: false, saving: false };
  const setDraft = (noteId, patch) => setDrafts((prev) => ({ ...prev, [noteId]: { ...getDraft(noteId), ...patch } }));

  const handleSaveComment = async (noteId) => {
    const d = getDraft(noteId);
    if (!d.text.trim() && d.docs.length === 0) { toast.error('Yorum metni veya belge girin'); return; }
    setDraft(noteId, { saving: true });
    try {
      await axios.post(`${API}/services/${serviceId}/notes/${noteId}/comments`, { text: d.text, documents: d.docs }, { headers: getAuthHeaders() });
      setDraft(noteId, { text: '', docs: [], saving: false });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Yorum eklenemedi'); setDraft(noteId, { saving: false }); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Bu notu silmek istediğinize emin misiniz?')) return;
    try { await axios.delete(`${API}/services/${serviceId}/notes/${noteId}`, { headers: getAuthHeaders() }); toast.success('Not silindi'); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Silinemedi'); }
  };
  const deleteComment = async (noteId, commentId) => {
    try { await axios.delete(`${API}/services/${serviceId}/notes/${noteId}/comments/${commentId}`, { headers: getAuthHeaders() }); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Silinemedi'); }
  };

  const canDelete = (createdBy) => isManager || createdBy === currentUserId;

  if (loading) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="service-notes">
      {canPost && (
        <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4 space-y-3" data-testid="note-form">
          <div className="flex items-center gap-2 text-amber-800 font-semibold">
            <StickyNote className="h-4 w-4" /> Yeni Günlük Not
          </div>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="İş ile ilgili günlük notunuzu yazın..." className="bg-white" data-testid="note-text" />
          {noteDocs.length > 0 && <DocChips docs={noteDocs} onRemove={(i) => setNoteDocs((p) => p.filter((_, idx) => idx !== i))} testidPrefix="note-doc-form" />}
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-1.5 text-xs text-amber-700 border border-amber-300 rounded px-2.5 py-1.5 cursor-pointer hover:bg-amber-100 w-fit" data-testid="note-doc-upload-label">
              {uploadingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Belge Ekle
              <input type="file" multiple className="hidden" disabled={uploadingNote} data-testid="note-doc-input"
                onChange={(e) => { uploadFiles(e.target.files, setUploadingNote, (doc) => setNoteDocs((p) => [...p, doc])); e.target.value = ''; }} />
            </label>
            <Button onClick={handleSaveNote} disabled={savingNote || uploadingNote} className="bg-amber-600 hover:bg-amber-700 gap-2" data-testid="note-save-btn">
              {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Not Ekle
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center gap-2 text-gray-700 font-semibold text-sm">
          <MessageSquare className="h-4 w-4 text-gray-500" /> Notlar ({notes.length})
        </div>
        {notes.length === 0 ? (
          <p className="text-gray-400 text-sm">Henüz not eklenmemiş.</p>
        ) : (
          <div className="space-y-3" data-testid="notes-list">
            {notes.map((note) => {
              const d = getDraft(note.id);
              return (
                <div key={note.id} className="border rounded-lg p-3 bg-white" data-testid={`note-item-${note.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-amber-700 text-sm">{note.created_by_name || 'Kullanıcı'}</span>
                        <span className="text-xs text-gray-400">{fmtDateTime(note.created_at)}</span>
                      </div>
                      {note.text && <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.text}</p>}
                      {note.documents?.length > 0 && <div className="mt-2"><DocChips docs={note.documents} testidPrefix={`note-doc-${note.id}`} /></div>}
                    </div>
                    {canDelete(note.created_by) && (
                      <button type="button" onClick={() => deleteNote(note.id)} className="text-red-400 hover:text-red-600" data-testid={`note-delete-${note.id}`}><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>

                  {/* Yorumlar */}
                  {note.comments?.length > 0 && (
                    <div className="mt-3 pl-3 border-l-2 border-gray-100 space-y-2">
                      {note.comments.map((c) => (
                        <div key={c.id} className="bg-gray-50 rounded p-2" data-testid={`comment-${c.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-medium text-gray-700 text-xs">{c.created_by_name || 'Kullanıcı'}</span>
                                <span className="text-[11px] text-gray-400">{fmtDateTime(c.created_at)}</span>
                              </div>
                              {c.text && <p className="text-sm text-gray-600 whitespace-pre-wrap">{c.text}</p>}
                              {c.documents?.length > 0 && <div className="mt-1"><DocChips docs={c.documents} testidPrefix={`comment-doc-${c.id}`} /></div>}
                            </div>
                            {canDelete(c.created_by) && (
                              <button type="button" onClick={() => deleteComment(note.id, c.id)} className="text-red-300 hover:text-red-500" data-testid={`comment-delete-${c.id}`}><X className="h-3.5 w-3.5" /></button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Yorum ekleme */}
                  {canPost && (
                    <div className="mt-3 flex flex-col gap-2" data-testid={`comment-form-${note.id}`}>
                      <Textarea value={d.text} onChange={(e) => setDraft(note.id, { text: e.target.value })} rows={2} placeholder="Yorum ekle / ek belge koy..." className="bg-white text-sm" data-testid={`comment-text-${note.id}`} />
                      {d.docs.length > 0 && <DocChips docs={d.docs} onRemove={(i) => setDraft(note.id, { docs: d.docs.filter((_, idx) => idx !== i) })} testidPrefix={`comment-doc-form-${note.id}`} />}
                      <div className="flex items-center justify-between">
                        <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 border rounded px-2 py-1 cursor-pointer hover:bg-gray-50 w-fit" data-testid={`comment-doc-upload-${note.id}`}>
                          {d.uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                          Belge
                          <input type="file" multiple className="hidden" disabled={d.uploading}
                            onChange={(e) => { uploadFiles(e.target.files, (v) => setDraft(note.id, { uploading: v }), (doc) => setDraft(note.id, { docs: [...getDraft(note.id).docs, doc] })); e.target.value = ''; }} />
                        </label>
                        <Button size="sm" variant="outline" onClick={() => handleSaveComment(note.id)} disabled={d.saving || d.uploading} className="gap-1.5 text-amber-700 border-amber-300" data-testid={`comment-save-${note.id}`}>
                          {d.saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />} Yorum Ekle
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceNotes;
